import { useCallback, useEffect, useState } from "react";
import { Modal } from "../../components/Modal";
import {
  getDuplicateSuppliers,
  mergeSuppliers,
  type DuplicateSupplierGroup,
} from "../../fornecedores/fornecedoresApi";

interface DuplicateSuppliersModalProps {
  onClose: () => void;
  // Called after a successful merge so the parent can reload its supplier list.
  onMerged: () => void;
}

const matchTypeLabel = (matchType: DuplicateSupplierGroup["matchType"]): string =>
  matchType === "tax_id" ? "Mesmo NIF" : "Mesmo nome";

// Stable key per group: matchType + key uniquely identifies a duplicate cluster.
const groupKey = (group: DuplicateSupplierGroup): string => `${group.matchType}:${group.key}`;

export function DuplicateSuppliersModal({ onClose, onMerged }: DuplicateSuppliersModalProps) {
  const [groups, setGroups] = useState<DuplicateSupplierGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Chosen survivor id per group key.
  const [survivorByGroup, setSurvivorByGroup] = useState<Record<string, number>>({});
  // Inline confirm state (no window.confirm — it blocks the test harness).
  const [confirmingGroup, setConfirmingGroup] = useState<string | null>(null);
  const [mergingGroup, setMergingGroup] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDuplicateSuppliers();
      setGroups(data);
      // Default survivor = first supplier of each group.
      const defaults: Record<string, number> = {};
      data.forEach((group) => {
        if (group.suppliers.length > 0) {
          defaults[groupKey(group)] = group.suppliers[0].id;
        }
      });
      setSurvivorByGroup(defaults);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os duplicados.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleConsolidate = async (group: DuplicateSupplierGroup) => {
    const key = groupKey(group);
    const survivorId = survivorByGroup[key];
    if (survivorId == null) return;
    const loserIds = group.suppliers
      .map((supplier) => supplier.id)
      .filter((id) => id !== survivorId);
    if (loserIds.length === 0) return;

    setMergingGroup(key);
    setError(null);
    try {
      await mergeSuppliers(survivorId, loserIds);
      setConfirmingGroup(null);
      onMerged();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível consolidar os fornecedores.");
    } finally {
      setMergingGroup(null);
    }
  };

  return (
    <Modal title="Fornecedores duplicados" onClose={onClose} maxWidthClass="max-w-2xl">
      <p className="text-secondary-text text-sm">
        Consolidar reaponta todas as referências dos duplicados para o fornecedor escolhido e
        elimina os restantes. Esta ação é irreversível.
      </p>

      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {isLoading ? (
        <p className="text-secondary-text text-sm py-6 text-center">A carregar...</p>
      ) : groups.length === 0 ? (
        <p className="text-secondary-text text-sm py-6 text-center">Sem duplicados detetados.</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const key = groupKey(group);
            const survivorId = survivorByGroup[key];
            const isConfirming = confirmingGroup === key;
            const isMerging = mergingGroup === key;
            return (
              <div
                key={key}
                className="border border-border bg-muted p-4 space-y-3"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {matchTypeLabel(group.matchType)}
                  </span>
                  <span className="text-foreground text-sm font-medium break-words">{group.key}</span>
                  <span className="text-secondary-text text-xs">
                    {group.suppliers.length} fornecedor(es)
                  </span>
                </div>

                <div className="space-y-1">
                  {group.suppliers.map((supplier) => (
                    <label
                      key={supplier.id}
                      className="flex items-center gap-3 p-2 bg-card border border-border cursor-pointer"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <input
                        type="radio"
                        name={`survivor-${key}`}
                        checked={survivorId === supplier.id}
                        disabled={isMerging}
                        onChange={() =>
                          setSurvivorByGroup((prev) => ({ ...prev, [key]: supplier.id }))
                        }
                        className="h-4 w-4 flex-shrink-0"
                        style={{ accentColor: "var(--primary)" }}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-foreground text-sm break-words">{supplier.name}</span>
                        <span className="block text-secondary-text text-xs">
                          {supplier.taxId ? `NIF ${supplier.taxId}` : "Sem NIF"}
                          {" · "}
                          {supplier.purchasesCount} compra(s)
                        </span>
                      </span>
                      {survivorId === supplier.id && (
                        <span className="text-primary text-xs whitespace-nowrap">Manter</span>
                      )}
                    </label>
                  ))}
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  {isConfirming ? (
                    <>
                      <span className="text-secondary-text text-sm">
                        Confirmar? Os restantes serão eliminados.
                      </span>
                      <button
                        type="button"
                        onClick={() => handleConsolidate(group)}
                        disabled={isMerging || survivorId == null}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ borderRadius: "var(--radius-button)" }}
                      >
                        {isMerging ? "A consolidar..." : "Confirmar consolidação"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingGroup(null)}
                        disabled={isMerging}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium border border-border text-foreground hover:bg-accent/30 transition-colors disabled:opacity-50"
                        style={{ borderRadius: "var(--radius-button)" }}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingGroup(key)}
                      disabled={isMerging || survivorId == null}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary border border-primary/20 hover:bg-accent/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ borderRadius: "var(--radius-button)" }}
                    >
                      Consolidar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
